import React, { useState, useRef } from "react";
import Toast from "../ui/Toast";

const MAX_LOGO_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/gif", "image/webp"];

/** Matches backend system fallback; default address when an import row has no street (any provider). */
const DEFAULT_ATT_IMPORT_ADDRESS = {
  street_address: "",
  city: "",
  state: "",
  zip: "",
  country: "",
};

const INPUT_CLASS =
  "w-full border border-gray-300 rounded-md px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--accent,#a51c1c)]/25 focus:border-[var(--accent,#a51c1c)] disabled:bg-gray-50 disabled:cursor-not-allowed";

const LABEL_CLASS = "block text-sm font-medium text-gray-700 mb-1.5";

const FormSection = ({ title, description, children, isFirst }) => (
  <div
    className={`grid grid-cols-1 md:grid-cols-[minmax(180px,28%)_1fr] gap-4 md:gap-10 py-7 ${
      isFirst ? "" : "border-t border-gray-200"
    }`}
  >
    <div className="md:pt-0.5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
    </div>
    <div className="space-y-4 min-w-0">{children}</div>
  </div>
);

const CreateTenantAdminModal = ({ isOpen, onClose, onSubmit }) => {
  const [tenantName, setTenantName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [attStreet, setAttStreet] = useState(DEFAULT_ATT_IMPORT_ADDRESS.street_address);
  const [attCity, setAttCity] = useState(DEFAULT_ATT_IMPORT_ADDRESS.city);
  const [attState, setAttState] = useState(DEFAULT_ATT_IMPORT_ADDRESS.state);
  const [attZip, setAttZip] = useState(DEFAULT_ATT_IMPORT_ADDRESS.zip);
  const [attCountry, setAttCountry] = useState(DEFAULT_ATT_IMPORT_ADDRESS.country);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const logoInputRef = useRef(null);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setLogoDataUrl(null);
      return;
    }
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setError("Please choose an image file (PNG, JPEG, GIF, or WebP).");
      setLogoDataUrl(null);
      return;
    }
    if (file.size > MAX_LOGO_SIZE_BYTES) {
      setError("Logo must be 10 MB or smaller.");
      setLogoDataUrl(null);
      return;
    }
    setError("");
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(reader.result);
    reader.onerror = () => {
      setError("Failed to read the image.");
      setLogoDataUrl(null);
    };
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setLogoDataUrl(null);
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation checks
    if (!tenantName.trim()) {
      setError("Tenant Name is required");
      return;
    }

    if (!firstName.trim()) {
      setError("First name is required");
      return;
    }

    if (!lastName.trim()) {
      setError("Last name is required");
      return;
    }

    if (!email.trim()) {
      setError("Email is required");
      return;
    }

    if (!validateEmail(email)) {
      setError("Please enter a valid Email address");
      return;
    }

    if (!password) {
      setError("Password is required");
      return;
    }

    if (password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }

    if (!confirmPassword) {
      setError("Confirm Password is required");
      return;
    }

    if (password !== confirmPassword) {
      setError("Password and Confirm Password do not match");
      return;
    }

    if (!logoDataUrl) {
      setError("Company Logo is required");
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        tenantName,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email,
        password,
        logo: logoDataUrl,
        role: "admin",
        attDefaultAddress: {
          street_address: attStreet.trim(),
          city: attCity.trim(),
          state: attState.trim(),
          zip: attZip.trim(),
          country: attCountry.trim(),
        },
      });
      // Reset form on success
      setTenantName("");
      setFirstName("");
      setLastName("");
      setEmail("");
      setPassword("");
      setConfirmPassword("");
      setShowPassword(false);
      setShowConfirmPassword(false);
      setLogoDataUrl(null);
      setAttStreet(DEFAULT_ATT_IMPORT_ADDRESS.street_address);
      setAttCity(DEFAULT_ATT_IMPORT_ADDRESS.city);
      setAttState(DEFAULT_ATT_IMPORT_ADDRESS.state);
      setAttZip(DEFAULT_ATT_IMPORT_ADDRESS.zip);
      setAttCountry(DEFAULT_ATT_IMPORT_ADDRESS.country);
      setError("");
      if (logoInputRef.current) {
        logoInputRef.current.value = "";
      }
      onClose();
    } catch (err) {
      // Handle specific error codes
      const errorMessage = err.message || "Failed to create tenant";

      if (errorMessage.includes("409") || errorMessage.toLowerCase().includes("already exists")) {
        setError("This Email or Tenant Name already exists. Please use a different one.");
      } else if (errorMessage.toLowerCase().includes("email")) {
        setError(`Email Error: ${errorMessage}`);
      } else if (errorMessage.toLowerCase().includes("password")) {
        setError(`Password Error: ${errorMessage}`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTenantName("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
    setShowPassword(false);
    setShowConfirmPassword(false);
    setLogoDataUrl(null);
    setAttStreet(DEFAULT_ATT_IMPORT_ADDRESS.street_address);
    setAttCity(DEFAULT_ATT_IMPORT_ADDRESS.city);
    setAttState(DEFAULT_ATT_IMPORT_ADDRESS.state);
    setAttZip(DEFAULT_ATT_IMPORT_ADDRESS.zip);
    setAttCountry(DEFAULT_ATT_IMPORT_ADDRESS.country);
    setError("");
    if (logoInputRef.current) {
      logoInputRef.current.value = "";
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-[999] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Create New Organization</h2>
          <button
            type="button"
            onClick={handleClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded focus:outline-none focus:ring-2 focus:ring-gray-300"
            disabled={loading}
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error ? (
          <Toast
            message={error}
            type="error"
            duration={5000}
            onClose={() => setError("")}
          />
        ) : null}

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto flex-1 px-8">
            {/* Tenant Details */}
            <FormSection
              isFirst
              title="Tenant Details"
              description="The core identification for this enterprise instance."
            >
              <div>
                <label className={LABEL_CLASS}>
                  Tenant Name <span className="text-[#D94436]">*</span>
                </label>
                <input
                  type="text"
                  value={tenantName}
                  onChange={(e) => setTenantName(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="Acme Corp"
                  disabled={loading}
                />
              </div>
            </FormSection>

            {/* Account Setup */}
            <FormSection
              title="Account Setup"
              description="The initial user account with full administrative privileges."
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>
                    First Name <span className="text-[#D94436]">*</span>
                  </label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="First name"
                    disabled={loading}
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>
                    Last Name <span className="text-[#D94436]">*</span>
                  </label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Last name"
                    disabled={loading}
                    autoComplete="family-name"
                  />
                </div>
              </div>
              <div>
                <label className={LABEL_CLASS}>
                  Email Address <span className="text-[#D94436]">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={INPUT_CLASS}
                  placeholder="admin@example.com"
                  disabled={loading}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={LABEL_CLASS}>
                    Password <span className="text-[#D94436]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${INPUT_CLASS} pr-10`}
                      placeholder="Minimum 8 characters"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      disabled={loading}
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
                <div>
                  <label className={LABEL_CLASS}>
                    Confirm Password <span className="text-[#D94436]">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`${INPUT_CLASS} pr-10`}
                      placeholder="Re-enter password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                      disabled={loading}
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                          />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </FormSection>

            {/* HQ Address */}
            <FormSection
              title="HQ Address"
              description="Physical billing and contact address for the organization."
            >
              <div>
                <label className={LABEL_CLASS}>Street Address</label>
                <input
                  type="text"
                  value={attStreet}
                  onChange={(e) => setAttStreet(e.target.value)}
                  className={INPUT_CLASS}
                  disabled={loading}
                  autoComplete="street-address"
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={LABEL_CLASS}>City</label>
                  <input
                    type="text"
                    value={attCity}
                    onChange={(e) => setAttCity(e.target.value)}
                    className={INPUT_CLASS}
                    disabled={loading}
                    autoComplete="address-level2"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>State</label>
                  <input
                    type="text"
                    value={attState}
                    onChange={(e) => setAttState(e.target.value)}
                    className={INPUT_CLASS}
                    disabled={loading}
                    autoComplete="address-level1"
                    placeholder="State"
                  />
                </div>
                <div>
                  <label className={LABEL_CLASS}>ZIP Code</label>
                  <input
                    type="text"
                    value={attZip}
                    onChange={(e) => setAttZip(e.target.value)}
                    className={INPUT_CLASS}
                    disabled={loading}
                    autoComplete="postal-code"
                    placeholder="ZIP"
                  />
                </div>
              </div>
              <div>
                <label className={LABEL_CLASS}>Country</label>
                <input
                  type="text"
                  value={attCountry}
                  onChange={(e) => setAttCountry(e.target.value)}
                  className={INPUT_CLASS}
                  disabled={loading}
                  autoComplete="country"
                  placeholder="Country"
                />
              </div>
            </FormSection>

            {/* Branding */}
            <FormSection
              title="Branding"
              description="Customize the interface appearance for this tenant."
            >
              <div>
                <label className={LABEL_CLASS}>
                  Company Logo <span className="text-[#D94436]">*</span>
                </label>
                <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
                    onChange={handleLogoChange}
                    className="sr-only"
                    disabled={loading}
                    id="tenant-logo-upload"
                  />
                  {logoDataUrl ? (
                    <div className="flex items-center gap-4">
                      <div className="w-24 h-24 border border-gray-200 rounded-md flex items-center justify-center bg-gray-50 overflow-hidden flex-shrink-0">
                        <img src={logoDataUrl} alt="Logo preview" className="max-h-full max-w-full object-contain p-1" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          onClick={() => logoInputRef.current?.click()}
                          className="text-sm font-medium text-gray-700 hover:text-gray-900"
                          disabled={loading}
                        >
                          Change image
                        </button>
                        <button
                          type="button"
                          onClick={clearLogo}
                          className="text-sm font-medium text-[#D94436] hover:text-[#c23d30] text-left"
                          disabled={loading}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        className="w-24 h-24 flex flex-col items-center justify-center gap-1 border-2 border-dashed border-gray-300 rounded-md text-gray-500 hover:border-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#D94436]/20"
                        disabled={loading}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                          />
                        </svg>
                        <span className="text-xs font-medium">Upload</span>
                      </button>
                      <div className="text-sm text-gray-600 pt-1">
                        <p>Upload a high-resolution PNG or SVG.</p>
                        <p className="text-xs text-gray-500 mt-1">
                          PNG, JPEG, GIF or WebP. Max 10 MB. Required for tenant login.
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </FormSection>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-8 py-5 border-t border-gray-200 flex-shrink-0 bg-white">
            <button
              type="button"
              onClick={handleClose}
              className="px-5 py-2.5 text-sm font-medium border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2.5 text-sm font-medium bg-[var(--accent,#a51c1c)] text-white rounded-md hover:bg-[#8c1a1a] disabled:bg-[var(--accent,#a51c1c)]/50 disabled:cursor-not-allowed transition-colors"
              disabled={loading}
            >
              {loading ? "Creating..." : "Create Tenant"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTenantAdminModal;
